import React, { createContext, useState, useContext } from 'react';
import axios from 'axios';
import {
  AddProductAPI,
  GetProductCategoryAPI,
  DeleteProductAPI,
  UploadImageCloudinaryAPI,
  UpdateProductAPI,
  UpdateProductStatusAPI,
} from './../api-services/Product.service';

import { localStorageKeys } from '../../utils/constants';

export const ProductDataContext = createContext();

// Module-level cache — survives re-renders and re-mounts; resets on full page refresh
let productCategoryCache = null;

const ProductDataProvider = ({ children }) => {
  const { REFRESH_TOKEN, TOKEN } = localStorageKeys;

  const [addProductData, setAddProductData] = useState({
    loading: false,
    data: null,
    success: null,
    errorMessage: '',
  });

  const [deleteProductData, setDeleteProductData] = useState({
    loading: false,
    data: null,
    success: null,
    errorMessage: '',
  });

  const [updateProductStatusData, setUpdateProductStatusData] = useState({
    loading: false,
    data: null,
    success: null,
    errorMessage: '',
  });

  const [uploadImage, setUploadImage] = useState({
    loading: false,
    data: null,
    success: null,
    errorMessage: '',
  });

  const [productCategories, setProductCategories] = useState({
    loading: false,
    data: null,
    success: null,
    errorMessage: '',
  });

  const onAddProduct = async (token, payload) => {
    const gettingToken = () => localStorage.getItem(TOKEN);
    if (gettingToken()) {
      axios.defaults.headers.common.Authorization = `Bearer ${gettingToken()}`;
    }

    try {
      await setAddProductData({
        loading: true,
        success: null,
        data: null,
        errorMessage: '',
      });

      const data = await AddProductAPI(token, payload);

      if (data.hasError) {
        await setAddProductData({
          loading: false,
          data: null,
          success: !data.hasError,
          errorMessage: data.message,
        });
        return data;
      }

      if (data.data.success) {
        await setAddProductData({
          loading: false,
          data: data?.data,
          success: true,
          errorMessage: '',
        });

        return data?.data;
      } else {
        await setAddProductData({
          loading: false,
          data: data?.data,
          success: data?.data?.success,
          errorMessage: data?.data?.CustomMessage,
        });

        return data?.data;
      }
    } catch (error) {
      console.log('---Catch black onAddProduct', error);
      setAddProductData({ loading: false, data: null, success: false, errorMessage: error });
    }
  };

  const onUploadImageCludinary = async (payload) => {
    const gettingToken = () => localStorage.getItem(TOKEN);
    const token = gettingToken();
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${gettingToken()}`;
    }

    try {
      await setUploadImage({
        loading: true,
        success: null,
        data: null,
        errorMessage: '',
      });

      const data = await UploadImageCloudinaryAPI(payload);

      if (data.hasError) {
        await setUploadImage({
          loading: false,
          data: null,
          success: !data.hasError,
          errorMessage: data.message,
        });
      }

      if (data.data.success) {
        await setUploadImage({
          loading: false,
          data: data,
          success: true,
          errorMessage: '',
        });
      } else {
        await setUploadImage({
          loading: false,
          data: data,
          success: data?.data?.success,
          errorMessage: data?.data?.CustomMessage,
        });
      }
      return data;
    } catch (error) {
      console.log('---Catch black onUploadImageCludinary', error);
      const data = { loading: false, data: null, success: false, errorMessage: error };
      setUploadImage(data);
      return data;
    }
  };

  const onGetProductCategory = async ({ forceRefresh = false } = {}) => {
    if (!forceRefresh && productCategoryCache) {
      setProductCategories({
        loading: false,
        data: productCategoryCache,
        success: true,
        errorMessage: '',
      });
      return;
    }

    const gettingToken = () => localStorage.getItem(TOKEN);
    if (gettingToken()) {
      axios.defaults.headers.common.Authorization = `Bearer ${gettingToken()}`;
    }

    try {
      setProductCategories({
        loading: true,
        success: null,
        data: null,
        errorMessage: '',
      });

      const data = await GetProductCategoryAPI();

      if (data.hasError) {
        setProductCategories({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message,
        });
        return data;
      }

      if (data?.data?.success) {
        productCategoryCache = data.data.data;
        setProductCategories({
          loading: false,
          data: productCategoryCache,
          success: true,
          errorMessage: '',
        });
      } else {
        setProductCategories({
          loading: false,
          data: null,
          success: false,
          errorMessage: data?.data?.message || 'Failed to fetch categories',
        });
      }

      return data?.data;
    } catch (error) {
      console.log('---Catch block onGetProductCategory', error);
      setProductCategories({ loading: false, data: null, success: false, errorMessage: error });
    }
  };

  const onDeleteProduct = async (payload) => {
    const gettingToken = () => localStorage.getItem(TOKEN);
    if (gettingToken()) {
      axios.defaults.headers.common.Authorization = `Bearer ${gettingToken()}`;
    }

    try {
      setDeleteProductData({
        loading: true,
        success: null,
        data: null,
        errorMessage: '',
      });

      const data = await DeleteProductAPI(payload);

      if (data.hasError) {
        setDeleteProductData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message,
        });
        return data;
      }

      if (data?.data?.success) {
        setDeleteProductData({
          loading: false,
          data: data?.data,
          success: true,
          errorMessage: '',
        });
        return data?.data;
      } else {
        setDeleteProductData({
          loading: false,
          data: data?.data,
          success: false,
          errorMessage: data?.data?.errors?.[0] || data?.data?.CustomMessage || 'Delete failed.',
        });
        return data?.data;
      }
    } catch (error) {
      console.log('---Catch block onDeleteProduct', error);
      setDeleteProductData({ loading: false, data: null, success: false, errorMessage: error });
    }
  };

  const onUpdateProduct = async (payload) => {
    const gettingToken = () => localStorage.getItem(TOKEN);
    const token = gettingToken();
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await UpdateProductAPI(payload);
      return response?.data || response;
    } catch (error) {
      console.log('---Catch block onUpdateProduct', error);
      return { success: false, error };
    }
  };

  const onUpdateProductStatus = async (payload) => {
    const gettingToken = () => localStorage.getItem(TOKEN);
    const token = gettingToken();
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${gettingToken()}`;
    }

    try {
      setUpdateProductStatusData({
        loading: true,
        success: null,
        data: null,
        errorMessage: '',
      });

      const data = await UpdateProductStatusAPI(payload);

      if (data.hasError) {
        setUpdateProductStatusData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message,
        });
        return data;
      }

      if (data?.data?.success) {
        productCategoryCache = data.data.data;
        setUpdateProductStatusData({
          loading: false,
          data: productCategoryCache,
          success: true,
          errorMessage: '',
        });
      } else {
        setUpdateProductStatusData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data?.data?.message || 'Failed to fetch categories',
        });
      }

      return data;
    } catch (error) {
      console.log('---Catch block onUpdateProductStatus', error);
      setUpdateProductStatusData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error,
      });
    }
  };

  return (
    <ProductDataContext.Provider
      value={{
        onAddProduct,
        onUpdateProduct,
        onUpdateProductStatus,
        setAddProductData,
        addProductData,
        onDeleteProduct,
        setDeleteProductData,
        deleteProductData,
        onUploadImageCludinary,
        setUploadImage,
        uploadImage,
        onGetProductCategory,
        productCategories,
        setProductCategories,
        setUpdateProductStatusData,
        updateProductStatusData,
      }}
    >
      {children}
    </ProductDataContext.Provider>
  );
};

export default ProductDataProvider;
